import React from 'react';
import { DUMMY_BUMDES } from './mocks/dplDummyData';

const VECIStatusBadge = ({ status }) => {
  const map = {
    APPROVED: { color: 'success', icon: 'bi-check-circle-fill', label: 'APPROVED' },
    SUBMITTED: { color: 'warning', icon: 'bi-clock-fill', label: 'SUBMITTED' },
    DRAFT: { color: 'secondary', icon: 'bi-pencil-fill', label: 'DRAFT' },
    'BELUM MULAI': { color: 'dark-subtle', icon: 'bi-dash-circle', label: 'BELUM MULAI' },
  };
  const config = map[status] || { color: 'secondary', icon: 'bi-dot', label: status };
  return (
    <span className={`badge bg-${config.color} bg-opacity-10 text-${config.color} border border-${config.color} border-opacity-25 rounded-pill px-3 py-1 extra-small fw-bold d-inline-flex align-items-center gap-1`}>
      <i className={`bi ${config.icon}`}></i> {config.label}
    </span>
  );
};

const DimensionProgress = ({ label, code, score, color }) => {
  const displayScore = score != null ? `${score.toFixed(1)} / 5.0` : '—';
  const progressPct = score != null ? (score / 5) * 100 : 0;
  return (
    <div className='mb-3'>
      <div className='d-flex align-items-center justify-content-between mb-1 small'>
        <span className='text-dark fw-semibold'>
          <span className='badge bg-light text-primary border me-1'>{code}</span>
          {label}
        </span>
        <span className='fw-bold text-dark'>{displayScore}</span>
      </div>
      <div className='progress' style={{ height: 6, borderRadius: 10 }}>
        <div
          className='progress-bar transition-all'
          style={{
            width: `${progressPct}%`,
            background: color || 'var(--bs-primary)',
            borderRadius: 10,
          }}
        ></div>
      </div>
    </div>
  );
};

const MonitoringVECI = () => {
  const veci = DUMMY_BUMDES.veci;

  const t0Score = veci.t0.score;
  const t1Score = veci.t1.score;
  const t2Score = veci.t2.score;

  const delta = t1Score && t0Score ? (t1Score - t0Score).toFixed(2) : null;

  const DIMENSION_LABELS = {
    OP: { name: 'Operasional (OP) *', color: '#4f46e5' },
    AD: { name: 'Adaptabilitas (AD) *', color: '#0891b2' },
    MP: { name: 'Kinerja Pasar (MP) *', color: '#059669' },
    S: { name: 'Strategis (S)', color: '#d97706' },
    E: { name: 'Keterhubungan (E)', color: '#7c3aed' },
  };

  return (
    <div className='card card-body border-0 shadow-sm rounded-4 mb-5'>
      {/* ── Page Header ── */}
      <div className='d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 mb-4'>
        <div className='d-flex align-items-center gap-3'>
          <div className='bg-primary bg-opacity-10 d-flex align-items-center justify-content-center rounded-3' style={{ width: 40, height: 40 }}>
            <i className='bi bi-clipboard-data-fill text-primary fs-5'></i>
          </div>
          <div>
            <h1 className='h5 fw-bold mb-0 text-dark'>Monitoring Survey VECI Lembaga</h1>
            <p className='text-muted mb-0 d-none d-lg-block' style={{ fontSize: '0.75rem' }}>
              Grafik dan perbandingan nilai VECI <strong>{DUMMY_BUMDES.name}</strong> pada fase T0, T1, dan T2 berdasarkan domain riil.
            </p>
          </div>
        </div>
        <span className='badge bg-primary bg-opacity-10 text-primary px-3 py-2 rounded-pill fw-semibold align-self-start' style={{ fontSize: '0.75rem' }}>
          <i className='bi bi-eye me-1'></i>View Only
        </span>
      </div>

      {/* ── Stat Widgets Row ── */}
      <div className='row g-3 mb-4'>
        {[
          { label: 'Baseline T0', value: t0Score ? `${t0Score.toFixed(2)} / 50` : '—', icon: 'bi-0-circle-fill', color: '#4f46e5' },
          { label: 'Mid T1', value: t1Score ? `${t1Score.toFixed(2)} / 50` : '—', icon: 'bi-1-circle-fill', color: '#0891b2' },
          { label: 'Final T2', value: t2Score ? `${t2Score.toFixed(2)} / 50` : '—', icon: 'bi-2-circle-fill', color: '#10b981' },
          { label: 'Peningkatan', value: delta ? `+${delta}` : '—', icon: 'bi-graph-up-arrow', color: '#059669' },
        ].map((s) => (
          <div className='col-6 col-md-3' key={s.label}>
            <div className='bg-light rounded-4 p-3 d-flex align-items-center gap-3 border border-light-subtle'>
              <div className='rounded-3 d-flex align-items-center justify-content-center flex-shrink-0' style={{ width: 40, height: 40, background: `${s.color}15` }}>
                <i className={`bi ${s.icon} fs-5`} style={{ color: s.color }}></i>
              </div>
              <div>
                <div className='fw-bold fs-5 text-dark mb-0'>{s.value}</div>
                <div className='text-muted extra-small fw-semibold'>{s.label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Main Phases Showcase ── */}
      <div className='row g-4 mb-4'>
        {[
          { key: 't0', title: 'Survey T0 (Baseline)', data: veci.t0 },
          { key: 't1', title: 'Survey T1 (Mid-Program)', data: veci.t1 },
          { key: 't2', title: 'Survey T2 (End-Program)', data: veci.t2 },
        ].map((phase) => {
          const { score, status, submittedAt, filler, dimensions } = phase.data;
          const isStarted = score != null;
          return (
            <div className='col-lg-4' key={phase.key}>
              <div className='card border border-light-subtle shadow-sm rounded-4 h-100 overflow-hidden'>
                <div className='p-3 border-bottom bg-light d-flex align-items-center justify-content-between'>
                  <span className='fw-bold text-dark small'>{phase.title}</span>
                  <VECIStatusBadge status={status} />
                </div>
                <div className='card-body p-3 d-flex flex-column justify-content-between'>
                  <div>
                    {isStarted ? (
                      <div className='d-flex align-items-baseline gap-1 mb-3'>
                        <span className='fs-3 fw-bold text-primary'>{score.toFixed(2)}</span>
                        <span className='text-muted extra-small'>/ 50.00 poin</span>
                      </div>
                    ) : (
                      <div className='text-center py-4 my-2'>
                        <div className='text-muted mb-2'><i className='bi bi-clipboard-x fs-2 opacity-25'></i></div>
                        <span className='fw-semibold text-muted small d-block'>Belum Mulai</span>
                      </div>
                    )}

                    {isStarted && dimensions && (
                      <div className='mb-3'>
                        <div className='d-flex align-items-center justify-content-between mb-2'>
                          <h6 className='fw-bold text-dark mb-0 extra-small text-uppercase tracking-wider'>Breakdown Dimensi</h6>
                          <span className='text-muted extra-small'>Skala 1 - 5</span>
                        </div>
                        {Object.entries(dimensions).map(([code, val]) => {
                          const dimInfo = DIMENSION_LABELS[code] || { name: `Dimensi ${code}`, color: 'var(--bs-primary)' };
                          return (
                            <DimensionProgress
                              key={code}
                              code={code}
                              label={dimInfo.name}
                              score={val}
                              color={dimInfo.color}
                            />
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {isStarted && (
                    <div className='bg-light rounded-3 p-2 mt-2' style={{ fontSize: '0.7rem' }}>
                      <div className='d-flex justify-content-between mb-1'>
                        <span className='text-muted'>Pengisi:</span>
                        <span className='fw-semibold text-dark text-truncate' style={{ maxWidth: '140px' }}>{filler || '—'}</span>
                      </div>
                      <div className='d-flex justify-content-between'>
                        <span className='text-muted'>Tanggal:</span>
                        <span className='fw-semibold text-dark'>{submittedAt || '—'}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Educational Widget ── */}
      <div className='bg-primary bg-opacity-10 rounded-4 p-3 d-flex align-items-start gap-3 border border-primary border-opacity-25 shadow-none'>
        <i className='bi bi-info-circle-fill text-primary fs-4 mt-1'></i>
        <div style={{ fontSize: '0.8rem' }}>
          <h6 className='fw-bold text-primary mb-1'>Formula & Pengukuran VECI (Village Enterprise Capability Index)</h6>
          <p className='text-dark mb-2 opacity-75' style={{ lineHeight: '1.5' }}>
            Berdasarkan model instrumen VECI CLP, indeks kapasitas kelembagaan BUMDes dihitung menggunakan formula:
            <strong className='d-block my-1 text-primary'>Skor VECI = (OP + AD) * MP</strong>
            Di mana **OP** (Operasional), **AD** (Adaptabilitas), dan **MP** (Kinerja Pasar) diukur menggunakan instrumen skala 1-5. Dimensi **S** (Strategis) dan **E** (Keterhubungan) digunakan sebagai konteks pendukung survei.
          </p>
          <span className='text-muted small fst-italic'>* Tanda bintang (*) menandakan dimensi yang berkontribusi langsung pada perhitungan skor total VECI.</span>
        </div>
      </div>

      <style>{`
        .transition-all { transition: all 0.4s ease; }
        .extra-small { font-size: 0.72rem; }
      `}</style>
    </div>
  );
};

export default MonitoringVECI;
